import * as Dialog from "@radix-ui/react-dialog";
import * as Menu from "@radix-ui/react-dropdown-menu";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigation as useRouterNavigation } from "react-router";
import { useT, useLocale } from "../i18n/I18nProvider";
import { SUPPORTED_LOCALES } from "../i18n/runtime";
import { useTheme, type ThemeMode } from "../theme/ThemeProvider";
import { Icon } from "../ui/Icon";
import { BrandMark } from "./BrandMark";
import { useAuth, useMe } from "./auth";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { LicenseIndicator, LicenseBanner } from "./LicenseIndicator";
import { useClub } from "./club";
import { EditTextLayer, EditTextToggle } from "./wording/EditTextToggle";
import { useWordingSync } from "./wording/useWording";
import { NotificationBell } from "../modules/notifications/Bell";
import { activeLink, useMenu, type MenuLink, type VisibleMenuGroup } from "./registry";
import { RAIL_QUERY, useMediaQuery, WIDE_QUERY } from "./useMediaQuery";
import { useNavigate } from "react-router";
import { DashboardWizard } from "./dashboard/DashboardWizard";
import { WorkspaceFooter, PeopleModal, ChatBubble, VersionModal } from "./dashboard/WorkspaceChrome";
import type { ChatUser } from "./dashboard/queries";
import { PosFullscreen } from "../modules/pos/Fullscreen";
import { PosModeControl, usePosMode } from "../modules/pos/PosMode";

function Brand() {
  const { brandName, logoUrl } = useClub();
  const [failed, setFailed] = useState<string | null>(null); // a logo that cannot be loaded shows the ClubCore mark instead
  const logo = logoUrl && failed !== logoUrl ? logoUrl : null;
  return (
    <div className="cc-brand">
      <span className="cc-brand__mark">
        {logo ? <img className="cc-brand__logo" src={logo} alt={brandName} onError={() => setFailed(logo)} /> : <BrandMark />}
      </span>
      <span className="cc-brand__name">{brandName}</span>
    </div>
  );
}

const SIDEBAR_KEY = "clubcore.sidebar";

/** The sidebar as an icon rail or in full. Follows the width until the person chooses; their choice is kept on this device. */
function useRail(): [boolean, (rail: boolean) => void] {
  const narrow = useMediaQuery(RAIL_QUERY);
  const [chosen, setChosen] = useState<string | null>(() => {
    try { return localStorage.getItem(SIDEBAR_KEY); } catch { return null; }
  });
  return [chosen ? chosen === "rail" : narrow, (rail) => {
    setChosen(rail ? "rail" : "full");
    try { localStorage.setItem(SIDEBAR_KEY, rail ? "rail" : "full"); } catch { /* This visit only. */ }
  }];
}

const OPEN_KEY = "clubcore.nav.open";

function rememberedOpen(): string[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(OPEN_KEY) ?? "[]");
    return Array.isArray(value) ? value.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function LinkBadge({ link }: { link: MenuLink }) {
  const Badge = link.entry.badge;
  return Badge ? <Badge /> : null;
}

function GroupLinks({ group, active, onNavigate }: { group: VisibleMenuGroup; active: MenuLink | undefined; onNavigate: (() => void) | undefined }) {
  const t = useT();
  return (
    <ul className="cc-nav__links">
      {group.links.map((link) => (
        <li key={link.entry.to}>
          <Link to={link.entry.to} className="cc-nav__link cc-nav__sublink" aria-current={active === link ? "page" : undefined} onClick={onNavigate}>
            <span>{t(link.labelKey)}</span>
            {link.entry.badge ? <link.entry.badge /> : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function Navigation({ onNavigate, rail = false }: { onNavigate?: () => void; rail?: boolean }) {
  const t = useT();
  const { today, groups, settings } = useMenu();
  const { pathname } = useLocation();
  const active = activeLink(pathname, groups);
  const inSettings = Boolean(activeLink(pathname, settings)) || pathname === "/settings";
  const [open, setOpen] = useState(rememberedOpen);
  const expanded = (id: string) => open.includes(id) || active?.group.id === id || groups.length <= 3;
  function toggle(id: string) {
    const next = expanded(id) ? open.filter((other) => other !== id) : [...open, id];
    setOpen(next);
    try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch { /* This visit only. */ }
  }
  return (
    <nav className="cc-nav" aria-label={t("nav.primary")}>
      <NavLink to="/" end className="cc-nav__link" title={rail ? t("nav.home") : undefined} onClick={onNavigate}>
        <Icon name="home" />
        <span>{t("nav.home")}</span>
      </NavLink>
      {today ? (
        <NavLink to={today} className="cc-nav__link" title={rail ? t("menu.today") : undefined} onClick={onNavigate}>
          <Icon name="clock" />
          <span>{t("menu.today")}</span>
        </NavLink>
      ) : null}
      {groups.map((group) => group.links.length === 1 ? (
        <NavLink key={group.id} to={group.links[0]!.entry.to} className="cc-nav__link" title={rail ? t(group.labelKey) : undefined} onClick={onNavigate}>
          <Icon name={group.icon} />
          <span>{t(group.links[0]!.labelKey)}</span>
          <LinkBadge link={group.links[0]!} />
        </NavLink>
      ) : rail ? (
        <Menu.Root key={group.id} modal={false}>
          <Menu.Trigger className="cc-nav__link cc-nav__section" title={t(group.labelKey)} data-active={active?.group.id === group.id || undefined}>
            <Icon name={group.icon} />
            <span className="cc-nav__section-label">{t(group.labelKey)}</span>
            {group.links.map((link) => link.entry.badge ? <link.entry.badge key={link.entry.to} /> : null)}
          </Menu.Trigger>
          <Menu.Portal>
            <Menu.Content className="cc-menu cc-section-menu" side="right" align="start" sideOffset={12} collisionPadding={12}>
              <Menu.Label className="cc-menu__label">{t(group.labelKey)}</Menu.Label>
              {group.links.map((link) => (
                <Menu.Item key={link.entry.to} asChild>
                  <Link to={link.entry.to} className="cc-menu__item cc-section-menu__link" aria-current={active?.link === link ? "page" : undefined} onClick={onNavigate}>
                    <Icon name={link.entry.icon} size={18} />
                    <span>{t(link.labelKey)}</span>
                    {link.entry.badge ? <link.entry.badge /> : null}
                  </Link>
                </Menu.Item>
              ))}
            </Menu.Content>
          </Menu.Portal>
        </Menu.Root>
      ) : (
        <div key={group.id} className="cc-nav__accordion">
          <button type="button" className="cc-nav__link cc-nav__section" aria-expanded={expanded(group.id)} data-active={active?.group.id === group.id || undefined} onClick={() => toggle(group.id)}>
            <Icon name={group.icon} />
            <span className="cc-nav__section-label">{t(group.labelKey)}</span>
            {expanded(group.id) ? null : group.links.map((link) => link.entry.badge ? <link.entry.badge key={link.entry.to} /> : null)}
            <Icon name={expanded(group.id) ? "chevronDown" : "chevron"} size={16} />
          </button>
          {expanded(group.id) ? <GroupLinks group={group} active={active?.link} onNavigate={onNavigate} /> : null}
        </div>
      ))}
      {settings.length ? (
        <Link to="/settings" className="cc-nav__link" aria-current={inSettings ? "page" : undefined} title={rail ? t("menu.settings") : undefined} onClick={onNavigate}>
          <Icon name="sliders" />
          <span>{t("menu.settings")}</span>
        </Link>
      ) : null}
    </nav>
  );
}

function ClubSwitcher() {
  const t = useT();
  const { club, memberships, isPlatformStaff, selectClub } = useClub();
  const navigate = useNavigate();
  const canSwitch = isPlatformStaff || memberships.length > 1;
  if (!canSwitch) return <span className="cc-club-chip">{club.name}</span>;
  return (
    <Menu.Root>
      <Menu.Trigger className="cc-club-chip cc-club-chip--button" aria-label={t("club.switchLabel", { club: club.name })}>
        <Icon name="building" size={18} />
        <span className="cc-club-chip__name">{club.name}</span>
        <Icon name="chevronDown" size={16} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content className="cc-menu" align="end" sideOffset={6}>
          <Menu.Label className="cc-menu__label">{t("club.switch")}</Menu.Label>
          {memberships.map((entry) => (
            <Menu.CheckboxItem
              key={entry.slug}
              className="cc-menu__item"
              checked={entry.slug === club.slug}
              onSelect={() => {
                if (entry.slug !== club.slug) {
                  selectClub(entry);
                  navigate("/");
                }
              }}
            >
              {entry.name}
              <Menu.ItemIndicator className="cc-menu__check">
                <Icon name="check" size={16} />
              </Menu.ItemIndicator>
            </Menu.CheckboxItem>
          ))}
          {isPlatformStaff ? (
            <>
              <Menu.Separator className="cc-menu__separator" />
              <Menu.Item className="cc-menu__item" onSelect={() => navigate("/choose-club")}>
                {t("club.allClubs")}
              </Menu.Item>
            </>
          ) : null}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

function UserMenu({ onChangePassword }: { onChangePassword: () => void }) {
  const t = useT();
  const me = useMe();
  const { signOut } = useAuth();
  const { mode, setMode } = useTheme();
  const { locale, setLocale } = useLocale();
  const initials = me.user.name
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const themes: { value: ThemeMode; label: string }[] = [
    { value: "system", label: t("theme.system") },
    { value: "light", label: t("theme.light") },
    { value: "dark", label: t("theme.dark") },
  ];
  return (
    <Menu.Root>
      <Menu.Trigger className="cc-avatar" aria-label={t("user.menu", { name: me.user.name })}>
        {initials}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content className="cc-menu" align="end" sideOffset={6}>
          <div className="cc-menu__label">{me.user.name}</div>
          <div className="cc-menu__label cc-menu__label--plain">{me.user.email}</div>
          <Menu.Separator className="cc-menu__separator" />
          <Menu.Label className="cc-menu__label">{t("theme.label")}</Menu.Label>
          <Menu.RadioGroup value={mode} onValueChange={(value) => setMode(value as ThemeMode)}>
            {themes.map((theme) => (
              <Menu.RadioItem key={theme.value} value={theme.value} className="cc-menu__item">
                <Icon name={theme.value === "dark" ? "moon" : "sun"} size={18} />
                {theme.label}
                <Menu.ItemIndicator className="cc-menu__check">
                  <Icon name="check" size={16} />
                </Menu.ItemIndicator>
              </Menu.RadioItem>
            ))}
          </Menu.RadioGroup>
          <Menu.Separator className="cc-menu__separator" />
          <Menu.Label className="cc-menu__label">{t("language.label")}</Menu.Label>
          <Menu.RadioGroup value={locale} onValueChange={(value) => setLocale(value as typeof locale)}>
            {SUPPORTED_LOCALES.map((entry) => (
              <Menu.RadioItem key={entry.code} value={entry.code} className="cc-menu__item" lang={entry.code}>
                <Icon name="globe" size={18} />
                {entry.label}
                <Menu.ItemIndicator className="cc-menu__check">
                  <Icon name="check" size={16} />
                </Menu.ItemIndicator>
              </Menu.RadioItem>
            ))}
          </Menu.RadioGroup>
          <Menu.Separator className="cc-menu__separator" />
          <Menu.Item className="cc-menu__item" onSelect={onChangePassword}>
            <Icon name="key" size={18} />
            {t("account.changePassword")}
          </Menu.Item>
          <Menu.Item className="cc-menu__item" onSelect={() => void signOut()}>
            <Icon name="logout" size={18} />
            {t("auth.signOut")}
          </Menu.Item>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

/** On a setup screen, the way back to the one Settings page that lists them. */
function SettingsBack() {
  const t = useT();
  const { settings } = useMenu();
  const { pathname } = useLocation();
  return activeLink(pathname, settings) ? (
    <Link to="/settings" className="cc-topbar__back">
      <Icon name="chevronLeft" size={18} />
      {t("menu.settings")}
    </Link>
  ) : null;
}

/** Moves focus to the new page's heading after navigation so keyboard and screen-reader users land at the top. */
function useFocusOnNavigate(main: React.RefObject<HTMLElement | null>) {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const heading = main.current?.querySelector<HTMLElement>("#page-title");
    (heading ?? main.current)?.focus({ preventScroll: false });
  }, [pathname, main]);
}

export function Shell() {
  const t = useT();
  const wide = useMediaQuery(WIDE_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rail, setRail] = useRail();
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const [chat, setChat] = useState<ChatUser | null>(null);
  const openPeople = () => { setDrawerOpen(false); setPeopleOpen(true); };
  const openVersion = () => { setDrawerOpen(false); setVersionOpen(true); };
  const [changingPassword, setChangingPassword] = useState(false);
  const main = useRef<HTMLElement>(null);
  const router = useRouterNavigation();
  const posMode = usePosMode();
  const { club } = useClub();
  const { pathname } = useLocation();
  useFocusOnNavigate(main);
  useWordingSync();

  if (posMode.locked || posMode.eligible) return (
    <div className="cc-pos-mode">
      <header className="cc-topbar"><Brand /><div className="cc-topbar__spacer" /><LicenseIndicator />{!posMode.locked ? <Link to="/" className="cc-button">{t("pos.register.backToClub")}</Link> : null}<PosFullscreen /><PosModeControl {...posMode} /></header>
      <LicenseBanner />
      <main id="main" className="cc-main" tabIndex={-1} ref={main}>{posMode.eligible ? <Outlet /> : null}</main>
    </div>
  );

  return (
    <div className={wide && rail ? "cc-shell cc-shell--rail" : "cc-shell"}>
      <a href="#main" className="skip-link" onClick={(event) => { event.preventDefault(); main.current?.focus(); }}>
        {t("a11y.skipToContent")}
      </a>
      {wide ? (
        <aside className="cc-sidebar">
          <Brand />
          <Navigation rail={rail} />
          <WorkspaceFooter onPeople={openPeople} onVersion={openVersion} />
          <button type="button" className="cc-icon-button cc-icon-button--nav cc-sidebar__toggle" aria-label={t(rail ? "nav.expand" : "nav.collapse")} aria-expanded={!rail} onClick={() => setRail(!rail)}>
            <Icon name={rail ? "chevron" : "chevronLeft"} />
          </button>
        </aside>
      ) : (
        <Dialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="cc-overlay" />
            <Dialog.Content className="cc-sidebar cc-sidebar--drawer" aria-describedby={undefined}>
              <Dialog.Title className="sr-only">{t("nav.primary")}</Dialog.Title>
              <div className="cc-sidebar__top">
                <Brand />
                <Dialog.Close className="cc-icon-button cc-icon-button--nav" aria-label={t("common.close")}>
                  <Icon name="close" />
                </Dialog.Close>
              </div>
              <Navigation onNavigate={() => setDrawerOpen(false)} />
              <WorkspaceFooter onPeople={openPeople} onVersion={openVersion} />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
      <div className="cc-column">
        <header className="cc-topbar">
          {wide ? null : (
            <button type="button" className="cc-icon-button" aria-label={t("nav.open")} onClick={() => setDrawerOpen(true)}>
              <Icon name="menu" />
            </button>
          )}
          <SettingsBack />
          <div className="cc-topbar__spacer" />
          {posMode.eligible ? <PosModeControl {...posMode} /> : null}
          <LicenseIndicator />
          <EditTextToggle />
          <NotificationBell onMessage={setChat} />
          <ClubSwitcher />
          <UserMenu onChangePassword={() => setChangingPassword(true)} />
        </header>
        <LicenseBanner />
        <div className={router.state === "loading" ? "cc-progress cc-progress--active" : "cc-progress"} role="presentation" />
        <main id="main" className={`cc-main${pathname === "/" ? " cc-main--home" : ""}`} tabIndex={-1} ref={main}>
          <Outlet />
        </main>
      </div>
      <ChangePasswordDialog open={changingPassword} onOpenChange={setChangingPassword} />
      <EditTextLayer />
      {pathname === "/" ? <DashboardWizard key={club.slug} /> : null}
      <PeopleModal open={peopleOpen} onClose={() => setPeopleOpen(false)} onChat={setChat} />
      <VersionModal open={versionOpen} onClose={() => setVersionOpen(false)} />
      {chat ? <ChatBubble key={chat.id} user={chat} onClose={() => setChat(null)} /> : null}
    </div>
  );
}
